import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  PaziaInput,
  PaziaSelect,
  PaziaFileDropzone,
  PaziaFormButton,
  PaziaMultiSelect,
  PaziaTextarea,
} from "components/atoms";
import { CourseGroupApi } from "api";
import { FormProvider, useForm } from "react-hook-form";

import { useToaster } from "utils";
import { useCategoriesOptions } from "api/categories";
import { useAdminUsersOptions } from "api/admin-users/useAdminUsers";

interface CourseGroupBaseFormProps {
  courseGroup?: CourseGroupApi;
  onSubmit: (data: CourseGroupFormSchema) => Promise<void>;
}
export const CourseGroupBaseForm = ({ onSubmit, courseGroup }: CourseGroupBaseFormProps): JSX.Element => {
  const { showWarning } = useToaster();
  const { loading, options: categories } = useCategoriesOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    context: { savedSlug: courseGroup?.slug },
    defaultValues: {
      categoryId: courseGroup ? courseGroup.category.id : undefined,
      name: courseGroup ? courseGroup.name : "",
      slug: courseGroup ? courseGroup.slug : "",
      mainImage: courseGroup ? courseGroup.mainImage : "",
      ogImageAlt: courseGroup ? (courseGroup.ogImageAlt ?? "") : "",
      ogDescription: courseGroup?.ogDescription ?? "",
      onlineMeetingUrl: courseGroup?.onlineMeetingUrl ?? "",
      onlineMeetingPassword: courseGroup?.onlineMeetingPassword ?? "",
      trainerOnlineMeetingUrl: courseGroup?.trainerOnlineMeetingUrl ?? "",
    },
  });

  const { handleSubmit } = methods;

  const onFileToLarge = () => {
    showWarning("Zdjęcie jest za duże. Maksymalny rozmiar to 5MB.");
  };
  return (
    <div className="p-2">
      <div className="mb-6">
        <h2 className="mb-2 text-xl font-semibold text-slate-800 dark:text-slate-100">Dane podstawowe</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">Podstawowe informacje o grupie kursów</p>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <PaziaSelect
              label="Kategoria"
              name={FORM_NAMES.CATEGORY_ID}
              options={categories}
              required
              disabled={loading}
              placeholder="Wybierz kategorię..."
            />

            <PaziaInput
              required
              label="Nazwa grupy kursów"
              name={FORM_NAMES.NAME}
              placeholder="Wprowadź nazwę grupy kursów..."
            />

            <PaziaInput
              label="Slug (adres URL)"
              name={FORM_NAMES.SLUG}
              placeholder="Pozostaw puste, aby wygenerować automatycznie z nazwy..."
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <PaziaFileDropzone
                name={FORM_NAMES.MAIN_IMAGE}
                label="Zdjęcie główne"
                required
                type="image"
                onFileToLarge={onFileToLarge}
                className="flex flex-col"
              />

              <div className="space-y-4">
                <PaziaInput
                  label="OG Image Alt (opis obrazka)"
                  name={FORM_NAMES.OG_IMAGE_ALT}
                  placeholder="Opis obrazka dla mediów społecznościowych..."
                />

                <PaziaTextarea
                  label="OG Description (opis strony dla wyszukiwarek i mediów społecznościowych)"
                  name={FORM_NAMES.OG_DESCRIPTION}
                  placeholder="Wprowadź opis strony grupy kursów..."
                  rows={3}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <PaziaInput
                label="Link do zajęć online dla kursantów (np. ClickMeeting)"
                name={FORM_NAMES.ONLINE_MEETING_URL}
                placeholder="https://..."
                tooltip="Widoczny dla zapisanych uczestników w strefie studenta (przy wymogu pełnej płatności dopiero po opłaceniu)."
              />
              <PaziaInput
                label="Link do zajęć online dla trenera"
                name={FORM_NAMES.TRAINER_ONLINE_MEETING_URL}
                placeholder="https://..."
                tooltip="Widoczny tylko dla trenerów przypisanych do kursu, w panelu trenera. Kursanci go nie widzą. Jeśli puste, trener zobaczy link dla kursantów."
              />
              <PaziaInput
                label="Hasło do pokoju"
                name={FORM_NAMES.ONLINE_MEETING_PASSWORD}
                placeholder="Pozostaw puste, jeśli pokój nie wymaga hasła"
                tooltip="Wspólne dla kursantów i trenera, pokazywane pod linkiem."
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end dark:border-slate-700">
            <button
              type="button"
              className="order-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 sm:order-1 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Anuluj
            </button>
            <PaziaFormButton title="Zapisz zmiany" type="success" className="order-1 sm:order-2" />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

const FORM_NAMES = {
  CATEGORY_ID: "categoryId",
  NAME: "name",
  SLUG: "slug",
  MAIN_IMAGE: "mainImage",
  OG_IMAGE_ALT: "ogImageAlt",
  OG_DESCRIPTION: "ogDescription",
  ONLINE_MEETING_URL: "onlineMeetingUrl",
  ONLINE_MEETING_PASSWORD: "onlineMeetingPassword",
  TRAINER_ONLINE_MEETING_URL: "trainerOnlineMeetingUrl",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.CATEGORY_ID]: yup.number().required(),
    [FORM_NAMES.NAME]: yup.string().required(),
    // Empty = generated from the name by the API. A typed slug follows the API's rule (one clean storefront URL
    // segment); a slug saved before the rule existed may be kept unchanged
    [FORM_NAMES.SLUG]: yup
      .string()
      .optional()
      .test(
        "slug-format",
        "Slug może zawierać tylko małe litery bez polskich znaków, cyfry i pojedyncze myślniki",
        (value, ctx) => !value || value === ctx.options.context?.savedSlug || /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value),
      ),
    [FORM_NAMES.MAIN_IMAGE]: yup.mixed().required(),
    [FORM_NAMES.OG_IMAGE_ALT]: yup.string().optional(),
    [FORM_NAMES.OG_DESCRIPTION]: yup.string().optional(),
    [FORM_NAMES.ONLINE_MEETING_URL]: yup
      .string()
      .trim()
      .optional()
      .matches(/^https?:\/\/\S+$/i, { message: "Podaj poprawny adres zaczynający się od http:// lub https://", excludeEmptyString: true }),
    [FORM_NAMES.ONLINE_MEETING_PASSWORD]: yup.string().trim().optional(),
    [FORM_NAMES.TRAINER_ONLINE_MEETING_URL]: yup
      .string()
      .trim()
      .optional()
      .matches(/^https?:\/\/\S+$/i, { message: "Podaj poprawny adres zaczynający się od http:// lub https://", excludeEmptyString: true }),
  })
  .required();

export type CourseGroupFormSchema = yup.InferType<typeof schema>;
